import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, MoreHorizontal, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getAccounts } from '@/features/accounts/api';
import {
  useCreateMaintenanceEvent,
  useDeleteMaintenanceEvent,
  useMaintenanceEventsList,
  useRunMaintenanceEventNow,
  useUpdateMaintenanceEvent,
} from '../hooks';
import type { MaintenanceEvent, MaintenanceEventInput } from '../types';

const ALL_ACCOUNTS = '_all';

interface FormState {
  accountId: string;
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  isTestEvent: boolean;
}

const emptyForm: FormState = {
  accountId: '',
  title: '',
  description: '',
  startsAt: '',
  endsAt: '',
  isActive: true,
  isTestEvent: false,
};

function toLocalInputValue(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInputValue(value: string): string {
  return new Date(value).toISOString();
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function relativeTime(value: string): string {
  const diff = new Date(value).getTime() - Date.now();
  const abs = Math.abs(diff);
  const days = Math.round(abs / 86400000);
  const hours = Math.round(abs / 3600000);
  const label = days >= 1 ? `${days}d` : `${hours}h`;
  return diff >= 0 ? `in ${label}` : `${label} ago`;
}

function statusFor(event: MaintenanceEvent): { label: string; variant: 'success' | 'warning' | 'secondary' | 'outline' } {
  if (!event.isActive) return { label: 'inactive', variant: 'secondary' };
  const starts = new Date(event.startsAt).getTime();
  if (starts < Date.now()) return { label: 'past', variant: 'outline' };
  if (starts - Date.now() <= 86400000) return { label: 'soon', variant: 'warning' };
  return { label: 'active', variant: 'success' };
}

function reminderStatus(value: string | null) {
  return value ? <Badge variant="success">sent</Badge> : <Badge variant="outline">pending</Badge>;
}

function buildInput(form: FormState): MaintenanceEventInput {
  return {
    accountId: form.accountId === ALL_ACCOUNTS ? null : form.accountId,
    title: form.title.trim(),
    description: form.description.trim() || null,
    startsAt: fromLocalInputValue(form.startsAt),
    endsAt: form.endsAt ? fromLocalInputValue(form.endsAt) : null,
    isActive: form.isActive,
    isTestEvent: form.isTestEvent,
  };
}

function formFromEvent(event: MaintenanceEvent): FormState {
  return {
    accountId: event.accountId ?? ALL_ACCOUNTS,
    title: event.title,
    description: event.description ?? '',
    startsAt: toLocalInputValue(event.startsAt),
    endsAt: toLocalInputValue(event.endsAt),
    isActive: event.isActive,
    isTestEvent: event.isTestEvent,
  };
}

export function MaintenanceEventsPage() {
  const [accountFilter, setAccountFilter] = useState(ALL_ACCOUNTS);
  const [titleFilter, setTitleFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<MaintenanceEvent | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<MaintenanceEvent | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const accountIdParam = accountFilter === ALL_ACCOUNTS ? undefined : accountFilter;
  const { data: events, isLoading } = useMaintenanceEventsList(accountIdParam);
  const createEvent = useCreateMaintenanceEvent();
  const updateEvent = useUpdateMaintenanceEvent();
  const deleteEvent = useDeleteMaintenanceEvent();
  const runEventNow = useRunMaintenanceEventNow();

  const { data: accountsData } = useQuery({
    queryKey: ['accounts', 'picker', 'maintenance-events'],
    queryFn: () => getAccounts({ pageNumber: 0, pageLength: 100, type: 'customer' }),
    staleTime: 60_000,
  });

  const accountOptions = useMemo(() => {
    return (accountsData?.accounts ?? [])
      .map((account) => ({ id: account.id, label: account.name || account.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accountsData]);

  const accountLookup = useMemo(() => {
    const map = new Map<string, string>();
    accountOptions.forEach((account) => map.set(account.id, account.label));
    return map;
  }, [accountOptions]);

  const filteredEvents = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    const needle = titleFilter.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((event) => event.title.toLowerCase().includes(needle));
  }, [events, titleFilter]);

  useEffect(() => {
    if (dialogOpen && !editingEvent && !form.accountId && accountFilter !== ALL_ACCOUNTS) {
      setForm((current) => ({ ...current, accountId: accountFilter }));
    }
  }, [accountFilter, dialogOpen, editingEvent, form.accountId]);

  const openCreate = () => {
    setEditingEvent(null);
    setForm({
      ...emptyForm,
      accountId: accountFilter === ALL_ACCOUNTS ? ALL_ACCOUNTS : accountFilter,
      isTestEvent: false,
    });
    setDialogOpen(true);
  };

  const openEdit = (event: MaintenanceEvent) => {
    setEditingEvent(event);
    setForm(formFromEvent(event));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingEvent(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const input = buildInput(form);
    if (editingEvent) {
      await updateEvent.mutateAsync({ id: editingEvent.id, patch: input });
    } else {
      await createEvent.mutateAsync(input);
    }
    closeDialog();
  };

  const toggleActive = (event: MaintenanceEvent) => {
    updateEvent.mutate({
      id: event.id,
      patch: { isActive: !event.isActive },
    });
  };

  const confirmDelete = async () => {
    if (!deletingEvent) return;
    await deleteEvent.mutateAsync(deletingEvent.id);
    setDeletingEvent(null);
  };

  const formBusy = createEvent.isPending || updateEvent.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-primary" />
            Maintenance Events
          </h1>
          <p className="text-muted-foreground">Schedule customer maintenance notices</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:w-64">
            <Select value={accountFilter} onValueChange={(value) => value && setAccountFilter(value)}>
              <SelectTrigger>
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ACCOUNTS}>All accounts</SelectItem>
                {accountOptions.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>2 weeks</TableHead>
              <TableHead>1 week</TableHead>
              <TableHead>3 days</TableHead>
              <TableHead>24h</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="w-12" />
            </TableRow>
            <TableRow className="bg-muted/30">
              <TableHead>
                <Input
                  placeholder="Filter..."
                  value={titleFilter}
                  onChange={(event) => setTitleFilter(event.target.value)}
                  className="h-8 text-xs"
                />
              </TableHead>
              <TableHead />
              <TableHead />
              <TableHead />
              <TableHead />
              <TableHead />
              <TableHead />
              <TableHead />
              <TableHead />
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={10}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">
                  No maintenance events found.
                </TableCell>
              </TableRow>
            ) : (
              filteredEvents.map((event) => {
                const status = statusFor(event);
                return (
                  <TableRow key={event.id}>
                    <TableCell>
                      <div className="font-medium">{event.title}</div>
                      {event.isTestEvent ? <Badge variant="warning">test</Badge> : null}
                      {event.description ? (
                        <div className="max-w-md truncate text-xs text-muted-foreground">
                          {event.description}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {event.accountId ? accountLookup.get(event.accountId) || event.accountId : 'All accounts'}
                    </TableCell>
                    <TableCell>
                      <div>{formatDateTime(event.startsAt)}</div>
                      <div className="text-xs text-muted-foreground">
                        {event.endsAt ? `until ${formatDateTime(event.endsAt)}` : relativeTime(event.startsAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </TableCell>
                    <TableCell>{reminderStatus(event.reminder2WeeksSentAt)}</TableCell>
                    <TableCell>{reminderStatus(event.reminder1WeekSentAt)}</TableCell>
                    <TableCell>{reminderStatus(event.reminder3DaysSentAt)}</TableCell>
                    <TableCell>{reminderStatus(event.reminder24hSentAt)}</TableCell>
                    <TableCell>
                      <Switch checked={event.isActive} onCheckedChange={() => toggleActive(event)} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {event.isTestEvent ? (
                            <DropdownMenuItem onClick={() => runEventNow.mutate(event.id)}>
                              <Play className="h-4 w-4 mr-2" />
                              Send test now
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem onClick={() => openEdit(event)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeletingEvent(event)}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent className="sm:max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Edit Maintenance Event' : 'New Maintenance Event'}</DialogTitle>
              <DialogDescription>
                Reminder emails are sent 2 weeks, 1 week, 3 days, and 24 hours before the start time.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Account</Label>
                <Select
                  value={form.accountId}
                  onValueChange={(value) => setForm((current) => ({ ...current, accountId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_ACCOUNTS}>All customer accounts</SelectItem>
                    {accountOptions.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="maintenance-title">Title</Label>
                <Input
                  id="maintenance-title"
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="maintenance-description">Description</Label>
                <Textarea
                  id="maintenance-description"
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maintenance-start">Start</Label>
                <Input
                  id="maintenance-start"
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(event) => setForm((current) => ({ ...current, startsAt: event.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maintenance-end">End</Label>
                <Input
                  id="maintenance-end"
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(event) => setForm((current) => ({ ...current, endsAt: event.target.value }))}
                />
              </div>

              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch
                  id="maintenance-active"
                  checked={form.isActive}
                  onCheckedChange={(checked) => setForm((current) => ({ ...current, isActive: !!checked }))}
                />
                <Label htmlFor="maintenance-active">Active</Label>
              </div>

              <div className="flex items-center gap-3 sm:col-span-2">
                <Checkbox
                  id="maintenance-test"
                  checked={form.isTestEvent}
                  onCheckedChange={(checked) => setForm((current) => ({ ...current, isTestEvent: !!checked }))}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="maintenance-test">Test event</Label>
                  <p className="text-xs text-muted-foreground">
                    When checked, all four reminder emails are sent immediately after creation.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={formBusy || !form.title || !form.startsAt}>
                {editingEvent ? 'Save' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingEvent} onOpenChange={(open) => !open && setDeletingEvent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete maintenance event?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the event and stops any unsent reminders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
